import React, { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry, themeQuartz, colorSchemeLight } from "ag-grid-community";
import { Box, Typography } from "@mui/material";
import { Plus, Edit, Trash2, Building2, CircleX } from "lucide-react";
import DeleteConfirmationPopup from "./DeleteConfirmation";
import AddEditCompanyModal from "./AddEditCompanyModal";

const companyGridTheme = themeQuartz.withPart(colorSchemeLight).withParams({
  backgroundColor: "#ffffff",
  foregroundColor: "#1f2937",
  accentColor: "#3b82f6",
  headerTextColor: "#ffffff",
  headerBackgroundColor: "#1a325d",
  oddRowBackgroundColor: "#f9fafb",
  chromeBackgroundColor: "#f9fafb",
});

ModuleRegistry.registerModules([AllCommunityModule]);

const CompanyTable = ({ companies, onDelete, onCompanyAdded }) => {
  const [search, setSearch] = useState("");
  const [deletePopup, setDeletePopup] = useState({
    isOpen: false,
    company: null,
  });
  const [addEditCompanyModalOpen, setAddEditCompanyModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const gridRef = useRef();
  const [logoModal, setLogoModal] = useState({ open: false, logoUrl: "" });

  const handleEdit = (company) => {
    setSelectedCompany(company);
    setAddEditCompanyModalOpen(true);
  };

  const handleDeleteClick = (company) => {
    setDeletePopup({ isOpen: true, company });
  };

  const handleDeleteConfirm = () => {
    if (deletePopup.company) {
      onDelete?.(deletePopup.company._id);
      setDeletePopup({ isOpen: false, company: null });
    }
  };

  const handleSearch = useCallback((e) => {
    setSearch(e.target.value);
    gridRef.current?.api?.setQuickFilter(e.target.value);
  }, []);

  const handleCompanySubmitSuccess = () => {
    setAddEditCompanyModalOpen(false);
    setSelectedCompany(null);
    onCompanyAdded?.();
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Logo",
        field: "logo",
        width: 100,
        cellRenderer: (params) => {
          return (
            <>
              {params.value ? (
                <div className="flex items-center justify-left h-full">
                  <img
                    src={`${process.env.REACT_APP_API_BASE_URL}/${params.data.logo}`}
                    alt="Company Logo"
                    className="w-10 h-10 rounded-full object-cover cursor-pointer"
                    onClick={() =>
                      setLogoModal({
                        open: true,
                        logoUrl: `${process.env.REACT_APP_API_BASE_URL}/${params.data.logo}`,
                      })
                    }
                  />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-gray-500" />
                </div>
              )}
            </>
          );
        },
        suppressMenu: true,
        sortable: false,
        filter: false,
      },
      {
        headerName: "Company Name",
        field: "name",
        flex: 1,
        cellRenderer: (params) => (
          <div className="flex items-center h-full">
            <span className="text-gray-700 text-sm font-medium">
              {params.value || "N/A"}
            </span>
          </div>
        ),
      },
      // {
      //   headerName: "Remark",
      //   field: "remark",
      //   flex: 1,
      //   cellRenderer: (params) => (
      //     <div className="flex items-center h-full">
      //       <span className="text-gray-700 text-sm font-medium">
      //         {params.value || "N/A"}
      //       </span>
      //     </div>
      //   ),
      // },
      {
        headerName: "Phone",
        field: "phone",
        flex: 1,
        cellRenderer: (params) => (
          <div className="flex items-center h-full">
            <span className="text-gray-700 text-sm font-medium">
              {params.value || "N/A"}
            </span>
          </div>
        ),
      },
      {
        headerName: "Email",
        field: "email",
        flex: 1,
        cellRenderer: (params) => (
          <div className="flex items-center h-full">
            <span className="text-gray-700 text-sm font-medium">
              {params.value || "N/A"}
            </span>
          </div>
        ),
      },
      {
        headerName: "Location",
        field: "location",
        flex: 1,
        cellRenderer: (params) => (
          <div className="flex items-center h-full">
            <span className="text-gray-700 text-sm font-medium">
              {params.value || "N/A"}
            </span>
          </div>
        ),
      },
      {
        headerName: "Address",
        field: "address",
        flex: 1,
        cellRenderer: (params) => (
          <div className="flex items-center h-full">
            <span className="text-gray-700 text-sm font-medium">
              {params.value || "N/A"}
            </span>
          </div>
        ),
      },
      {
        headerName: "Actions",
        width: 130,
        suppressMenu: true,
        sortable: false,
        filter: false,
        cellRenderer: (params) => (
          <div className="flex items-center gap-2 h-full">
            <button
              onClick={() => handleEdit(params.data)}
              className="group p-2 rounded-full bg-white shadow-md hover:bg-blue-100 transition-all duration-200 border border-blue-200 hover:shadow-blue-300"
            >
              <Edit className="w-4 h-4 text-blue-600 group-hover:scale-110 group-hover:text-blue-700 transition-all" />
            </button>

            <button
              onClick={() => handleDeleteClick(params.data)}
              className="group p-2 rounded-full bg-white shadow-md hover:bg-rose-100 transition-all duration-200 border border-rose-200 hover:shadow-rose-300"
            >
              <Trash2 className="w-4 h-4 text-rose-500 group-hover:scale-110 group-hover:text-rose-700 transition-all" />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{}}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        {/* Title with Icon */}
        <h2 className="flex items-center gap-2 text-[24px] font-semibold text-slate-800 tracking-tight">
          <Building2 className="w-6 h-6 text-[#27ade3]" strokeWidth={2} />
          Company Management
        </h2>

        {/* Add Company Button */}
        <button
          onClick={() => {
            setSelectedCompany(null);
            setAddEditCompanyModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-tr from-[#1a325d] to-[#27ade3] shadow-md hover:scale-105 transition-transform rounded-lg"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          Add Company
        </button>
      </div>

      {/* Table Container */}
      <Box
        className="ag-theme-quartz relative"
      >
        <AgGridReact
          ref={gridRef}
          theme={companyGridTheme}
          rowData={companies}
          columnDefs={columnDefs}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20, 50]}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
          }}
          domLayout="autoHeight"
          rowHeight={60}
          onGridReady={() => {
            setTimeout(() => {
              const header = document.querySelector(".ag-header");
              if (header) {
                header.style.background =
                  "linear-gradient(to top right, #1a325d, #27ade3)";
                header.style.color = "#ffffff";
              }
            }, 0);
          }}
        />
      </Box>

      {logoModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative max-w-4xl max-h-[90vh] p-4 bg-white rounded-lg">
            <button
              onClick={() => setLogoModal({ open: false, logoUrl: "" })}
              className="absolute -top-3 -right-3 bg-gray-200 hover:bg-gray-300 rounded-full p-2 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              aria-label="Close"
            >
              <CircleX className="w-5 h-5 text-black-500" />
            </button>

            <img
              src={logoModal.logoUrl}
              alt="Company Logo"
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>
        </div>
      )}

      {/* Modals */}
      <DeleteConfirmationPopup
        isOpen={deletePopup.isOpen}
        onClose={() => setDeletePopup({ isOpen: false, company: null })}
        onConfirm={handleDeleteConfirm}
        userName={deletePopup.company ? `${deletePopup.company.name}` : ""}
      />

      <AddEditCompanyModal
        open={addEditCompanyModalOpen}
        onClose={() => {
          setAddEditCompanyModalOpen(false);
          setSelectedCompany(null);
        }}
        onSubmit={handleCompanySubmitSuccess}
        company={selectedCompany}
      />
    </Box>
  );
};

export default CompanyTable;
